import { useEffect, useMemo, useRef, useState } from 'react';

import { CODE_PROGRAM_MAP, PROGRAM_CODE_MAP, resolveProgramName } from '../../../utils/dataNormalizer';
import { DETAIL_PANEL_TAB_SELECTIONS } from '../constants/detailPanelTabs';

const mergeStableRowState = (prev, next) => {
  if (!prev) return next;
  if (!next) return prev;

  const merged = { ...prev, ...next };

  if (Array.isArray(prev.productSelections) && prev.productSelections.length > 0) {
    if (!Array.isArray(next.productSelections) || next.productSelections.length === 0) {
      merged.productSelections = prev.productSelections;
    }
  }

  if (Array.isArray(prev.vendors) && prev.vendors.length > 0) {
    if (!Array.isArray(next.vendors) || next.vendors.length === 0) {
      merged.vendors = prev.vendors;
    }
  }

  merged._original = next._original ?? prev._original;
  return merged;
};

export const resolveProgramNameFromRow = (row) => {
  if (!row) return '';
  if (row.programCode && CODE_PROGRAM_MAP[row.programCode]) return CODE_PROGRAM_MAP[row.programCode];
  if (row.program && CODE_PROGRAM_MAP[row.program]) return CODE_PROGRAM_MAP[row.program];
  if (row.programName && PROGRAM_CODE_MAP[row.programName]) return row.programName;
  if (row.programLabel && PROGRAM_CODE_MAP[row.programLabel]) return row.programLabel;
  return (
    resolveProgramName(row.programName) ||
    resolveProgramName(row.programLabel) ||
    resolveProgramName(row.programCode) ||
    resolveProgramName(row.program) ||
    ''
  );
};

export const useDetailPanelRowState = (rowData) => {
  const [currentRow, setCurrentRow] = useState(rowData);
  const [activeTab, setActiveTab] = useState(DETAIL_PANEL_TAB_SELECTIONS);
  const [selectedSelectionIndex, setSelectedSelectionIndex] = useState(0);
  const [jsonCollapsed, setJsonCollapsed] = useState(false);

  // Track the current row's identity to detect when we're switching to a different row
  const currentRowIdRef = useRef(null);
  // If the row came from Status Changes, it may include a stable selectionKey we should focus after hydration.
  const selectionKeyTargetRef = useRef(null);
  // Used to ignore stale auto-hydrate responses when users rapidly switch rows.
  const autoHydrateKeyRef = useRef(null);

  useEffect(() => {
    // Create a unique identifier for the row (BAC + program)
    const rowId = rowData ? `${rowData.bac}-${rowData.programName || rowData.programLabel || rowData.programCode || ''}` : null;

    // Only reset tab if we're switching to a different row, not just updating the same row
    const isDifferentRow = currentRowIdRef.current !== rowId;

    setCurrentRow((prev) => (isDifferentRow ? rowData : mergeStableRowState(prev, rowData)));
    if (isDifferentRow) {
      setActiveTab(DETAIL_PANEL_TAB_SELECTIONS);
      currentRowIdRef.current = rowId;
    }
    selectionKeyTargetRef.current = rowData?.selectionKey || null;
    setJsonCollapsed(false);
    setSelectedSelectionIndex(0);
  }, [rowData]);

  const canonicalProgramNameRef = useRef(resolveProgramNameFromRow(rowData));

  useEffect(() => {
    const resolved = resolveProgramNameFromRow(rowData);
    if (resolved) {
      canonicalProgramNameRef.current = resolved;
    }
  }, [rowData]);

  const programKey = useMemo(() => {
    return (
      resolveProgramNameFromRow(currentRow) ||
      canonicalProgramNameRef.current ||
      ''
    );
  }, [currentRow]);

  const programCode =
    currentRow?.programCode || currentRow?.program || PROGRAM_CODE_MAP[programKey] || '';
  const programLabel = currentRow?.programLabel || programKey;

  return {
    currentRow,
    setCurrentRow,
    activeTab,
    setActiveTab,
    selectedSelectionIndex,
    setSelectedSelectionIndex,
    jsonCollapsed,
    setJsonCollapsed,
    selectionKeyTargetRef,
    autoHydrateKeyRef,
    programKey,
    programCode,
    programLabel,
  };
};
